/**
 * ImportedContractViewPage — viewer + scoped editor for contracts
 * produced by the R2 upload-and-extract flow.
 *
 * The contract's authoritative content is the uploaded PDF/DOCX, not a
 * JS template render. So we:
 *
 *   - render the **original document** (presigned URL) in the main
 *     pane instead of `<tmpl.Component values={values} mode="final" />`
 *   - show the extracted parties + property + scalars on the left
 *   - filter the template's variableSchema to fields tagged
 *     `scopes.imported` (see packages/templates/src/types.ts and
 *     docs/architecture/template-field-scopes.md)
 *
 * The contract's status is `signed` from the moment `imports.confirm`
 * runs (the upload IS signed — that's a real-world fact about the
 * artefact, not a workflow state). But the extracted scalars often
 * need completion before lifecycle reminders / reports work. We
 * therefore offer a **scoped edit mode** that only ever writes
 * `scopes.imported`-tagged fields through `contracts.update_imported_fields`,
 * which records a distinct `import.complete_extraction` audit event.
 * `contracts.update_draft` is NOT widened — keeping "signed = locked"
 * intact at the policy module and confining the post-signing write
 * surface to one narrow door.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  RENDERERS,
  importedRequiredForReview,
  resolveSectionTitle,
  type TemplateModule,
  type VariableField,
} from '@hyranai/regta-templates';
import { tenantApi, type ContractDetail } from '../api.js';
import type { LocalParty } from '../components/PartiesPanel.js';
import { useTenantSession } from '../auth-context.js';
import { PartiesPanel } from '../components/PartiesPanel.js';
import { ConnectedContractsBar } from '../components/ConnectedContractsPanel.js';
import { NotificationsTab } from '../components/NotificationsTab.js';
import { useDropCached, useInvalidate, useTenantQuery } from '../query.js';

const STATUS_LABEL: Record<ContractDetail['status'], string> = {
  draft: 'Utkast',
  pending: 'Att hantera',
  in_review: 'Granskas',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Visat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

function fmt(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

type SaveState = 'idle' | 'saving' | 'error';

export function ImportedContractViewPage({
  contract,
  tmpl,
  parties,
}: {
  contract: ContractDetail;
  tmpl: TemplateModule;
  parties: LocalParty[];
}) {
  const { token } = useTenantSession();
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const original = contract.originalDocument!; // guaranteed by the caller's dispatch.

  const persistedValues = contract.values as Record<string, unknown>;
  const allFields = tmpl.variableSchema as ReadonlyArray<VariableField<Record<string, unknown>>>;

  const importFields = useMemo(
    () => allFields.filter((f) => Boolean(f.scopes?.imported)),
    [allFields],
  );

  // Bucket into sections in declared order — same shape as the editor.
  // `f.section` is a section ref (ADR-0049); resolve the display title
  // via the template's declared sections (falls back to the raw string
  // for legacy templates).
  const sections = useMemo(() => {
    const out: Array<{
      section: string;
      title: string;
      fields: VariableField<Record<string, unknown>>[];
    }> = [];
    for (const f of importFields) {
      let bucket = out.find((s) => s.section === f.section);
      if (!bucket) {
        bucket = {
          section: f.section,
          title: resolveSectionTitle(tmpl.metadata.sections, f.section),
          fields: [],
        };
        out.push(bucket);
      }
      bucket.fields.push(f);
    }
    return out;
  }, [importFields, tmpl.metadata.sections]);

  // Required-for-review check runs against the *persisted* values, not
  // the in-progress edit buffer — the banner reflects what's saved.
  const missingRequired = useMemo(
    () =>
      importFields.filter(
        (f) => importedRequiredForReview(f) && isEmpty(persistedValues[f.key]),
      ),
    [importFields, persistedValues],
  );

  // Bevakning ("Påminnelser") is a real tab on imported contracts too —
  // an imported lease has lifecycle dates (uppsägning, upphörande) that
  // need monitoring regardless of whether any reminders exist yet.
  const [tab, setTab] = useState<'avtal' | 'paminnelser'>('avtal');
  const notificationsQ = useTenantQuery(
    'notifications.list_by_contract',
    (cid) =>
      tenantApi.notifications.list_by_contract({ contract_id: cid as string }),
    [contract.id],
    { revalidateOnFocus: false },
  );
  const reminderCount = (notificationsQ.data?.notifications ?? []).filter(
    (n) => !n.acknowledgedAt,
  ).length;

  // Edit-mode state. Stays local; only `Spara komplettering` persists.
  const [editing, setEditing] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const dropCached = useDropCached();

  const onDelete = useCallback(async () => {
    if (!token || deleting) return;
    if (
      !confirm(
        `Radera avtalet "${contract.title}" och det uppladdade dokumentet? Detta kan inte ångras.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setErr(null);
    try {
      await tenantApi.contracts.delete({ id: contract.id });
      // Drop (not just revalidate) the detail cache: a 404 refetch
      // would leave the stale contract rendered on browser-Back.
      await dropCached('contracts.get', contract.id);
      await invalidate('contracts.list');
      navigate('/contracts');
    } catch (e) {
      const msg = (e as Error).message;
      const sv: Record<string, string> = {
        delete_children_first:
          'Avtalet har kopplade avtal — radera eller arkivera dem först.',
        not_imported: 'Endast uppladdade avtal kan raderas.',
        mid_signing_cancel_first:
          'Avtalet är mitt i signering — avbryt signeringen först.',
        not_admin_or_creator:
          'Du har inte behörighet att radera det här avtalet.',
      };
      setErr(sv[msg] ?? `Det gick inte att radera avtalet (${msg}).`);
      setDeleting(false);
    }
  }, [token, deleting, contract.id, contract.title, dropCached, invalidate, navigate]);

  const enterEdit = useCallback(() => {
    setDraftValues({ ...persistedValues });
    setDirtyKeys(new Set());
    setErr(null);
    setEditing(true);
  }, [persistedValues]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraftValues({});
    setDirtyKeys(new Set());
    setErr(null);
  }, []);

  const onFieldChange = useCallback((key: string, next: unknown) => {
    setDraftValues((prev) => ({ ...prev, [key]: next }));
    setDirtyKeys((s) => {
      const n = new Set(s);
      n.add(key);
      return n;
    });
  }, []);

  const save = useCallback(async () => {
    if (!token) return;
    if (dirtyKeys.size === 0) {
      setEditing(false);
      return;
    }
    setSaveState('saving');
    setErr(null);
    try {
      const patch: Record<string, unknown> = {};
      for (const k of dirtyKeys) patch[k] = draftValues[k];
      const res = await tenantApi.contracts.update_imported_fields({
        id: contract.id,
        values: patch,
      });
      if (res.rejected.length > 0) {
        // Should never happen — UI only edits imported fields — but
        // surface it loudly if it does so we catch contract drift.
        // eslint-disable-next-line no-console
        console.warn('update_imported_fields rejected keys', res.rejected);
      }
      await invalidate('contracts.get', contract.id);
      setSaveState('idle');
      setEditing(false);
      setDirtyKeys(new Set());
    } catch (e) {
      setErr((e as Error).message);
      setSaveState('error');
    }
  }, [token, dirtyKeys, draftValues, contract.id, invalidate]);

  // Values the form renders from. In edit mode we layer the draft on
  // top of persisted; outside edit mode it's just persisted.
  const formValues = editing ? draftValues : persistedValues;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex flex-col gap-1 rounded border border-stone-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Link to="/contracts" className="text-xs text-stone-500 hover:underline">
              ← Avtal
            </Link>
            <h2 className="mt-1 text-sm font-medium">{contract.title}</h2>
          </div>
          <div className="flex flex-col items-end gap-1.5 text-right text-xs text-stone-500">
            <div>
              <span className="rounded bg-stone-100 px-2 py-0.5 font-medium text-stone-700">
                {STATUS_LABEL[contract.status] ?? contract.status}
              </span>
              <span className="ml-2">Importerat avtal</span>
            </div>
            <button
              type="button"
              onClick={() => void onDelete()}
              disabled={deleting}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              title="Radera avtalet och det uppladdade dokumentet permanent."
            >
              {deleting ? 'Raderar…' : 'Radera avtal'}
            </button>
          </div>
        </div>
        <p className="text-xs text-stone-500">
          Originaldokument: <span className="font-medium">{original.filename}</span>
        </p>
      </div>

      {/* Above the tab strip: the delete button lives in the always-
          visible header, so its errors must render on every tab. */}
      {err && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {err}
        </div>
      )}

      <div className="mb-4 flex gap-1 border-b border-stone-200">
        <button
          type="button"
          onClick={() => setTab('avtal')}
          className={
            'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
            (tab === 'avtal'
              ? 'border-stone-900 text-stone-900'
              : 'border-transparent text-stone-500 hover:text-stone-700')
          }
        >
          Avtal
        </button>
        <button
          type="button"
          onClick={() => setTab('paminnelser')}
          className={
            'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
            (tab === 'paminnelser'
              ? 'border-stone-900 text-stone-900'
              : 'border-transparent text-stone-500 hover:text-stone-700')
          }
        >
          Påminnelser{' '}
          <span className="font-normal text-stone-400">({reminderCount})</span>
        </button>
      </div>

      {tab === 'paminnelser' ? (
        <main className="flex flex-1 flex-col overflow-auto bg-white">
          <NotificationsTab contractId={contract.id} />
        </main>
      ) : (
        <>
      {/* Connected ("kopplade") avtal — an imported contract is a real
          contract row, so it can parent connected sub-documents (e.g. an
          avstående från besittningsskydd under an imported lokal-/bostads-
          hyresavtal) exactly like a drafted one. The backend already
          accepts a signed parent (contracts.create_from_template has no
          status gate); this surfaces the same affordance the editor shows.
          Not gated by the parent's signed state — connected avtal have
          their own lifecycle (ADR-0055), so `disabled={false}`. */}
      <ConnectedContractsBar
        token={token!}
        contractId={contract.id}
        templateKey={contract.templateKey}
        parentContractId={contract.parentContractId}
        parent={contract.parent}
        children={contract.children}
        disabled={false}
      />

      {/* Property auto-link / auto-create surface. Driven by warning
          codes the backend emits during import.confirm — see
          imports.ts. The intent is the bulk-upload happy path:
          confirm at a glance when extraction was clean, otherwise
          steer to the right CTA. */}
      <PropertyConfirmBanner original={original} contract={contract} />

      {/* Completion banner — red when there's missing required-for-review
          data, neutral once complete. Always offers an edit affordance
          so corrections stay possible after first complete. */}
      <div className="mb-4">
        {missingRequired.length > 0 ? (
          <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p className="font-medium">Inte komplett.</p>
            <p className="mt-1">
              Följande fält saknas och behöver kompletteras innan avtalet kan användas i påminnelser
              och rapporter:{' '}
              <span className="font-medium">
                {missingRequired.map((f) => f.label).join(', ')}
              </span>
              .
            </p>
            {!editing && (
              <button
                type="button"
                onClick={enterEdit}
                className="mt-2 rounded border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                Komplettera fälten
              </button>
            )}
          </div>
        ) : !editing ? (
          <div className="flex items-center justify-between gap-3 rounded border border-stone-200 bg-stone-50 px-4 py-2 text-xs text-stone-600">
            <span>Alla obligatoriska fält för det importerade avtalet är ifyllda.</span>
            <button
              type="button"
              onClick={enterEdit}
              className="rounded border border-stone-300 bg-white px-2 py-1 text-stone-700 hover:bg-stone-100"
            >
              Redigera komplettering
            </button>
          </div>
        ) : null}
      </div>

      {editing && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          <span>
            Redigeringsläge — endast extraherade fält och parter sparas. Originaldokumentet
            ändras inte.
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saveState === 'saving'}
              className="rounded bg-amber-700 px-2 py-1 font-medium text-white hover:bg-amber-800 disabled:opacity-60"
            >
              {saveState === 'saving' ? 'Sparar…' : 'Spara komplettering'}
            </button>
          </div>
        </div>
      )}

      <div className="grid flex-1 gap-6 grid-cols-1 lg:grid-cols-[420px_1fr]">
        <aside className="flex flex-col gap-5 overflow-auto pb-6">
          {/* Parties are policy-allowed to edit even after signing on
              an imported contract — see parties.ts:loadContractForWrite.
              Disable while saving to avoid racing the scalars patch. */}
          <PartiesPanel
            token={token!}
            contractId={contract.id}
            parties={parties}
            values={formValues}
            slots={tmpl.metadata.partySlots}
            variableSchema={allFields}
            onAddParty={() => undefined}
            onRemoveParty={() => undefined}
            onFieldChange={onFieldChange}
            disabled={!editing || saveState === 'saving'}
          />

          {sections.map((s) => (
            <section key={s.section} className="flex flex-col gap-2">
              <h3 className="border-b border-stone-200 pb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
                {s.title}
              </h3>
              {s.fields.map((f) => {
                if (editing) {
                  const Renderer = RENDERERS[f.type];
                  return (
                    <Renderer
                      key={f.key}
                      field={f}
                      value={formValues[f.key]}
                      onChange={(next) => onFieldChange(f.key, next)}
                      disabled={saveState === 'saving'}
                    />
                  );
                }
                const v = fmt(formValues[f.key]);
                const isMissing =
                  importedRequiredForReview(f) && isEmpty(formValues[f.key]);
                return (
                  <div
                    key={f.key}
                    className="flex items-baseline justify-between gap-3 border-b border-stone-100 pb-1.5"
                  >
                    <dt className="text-xs text-stone-500">{f.label}</dt>
                    <dd
                      className={`text-sm ${
                        v ? 'text-stone-900' : isMissing ? 'text-red-600' : 'text-stone-400'
                      }`}
                    >
                      {v ?? (isMissing ? 'Saknas' : '—')}
                    </dd>
                  </div>
                );
              })}
            </section>
          ))}

          {sections.length === 0 && (
            <div className="rounded border border-stone-200 bg-stone-50 p-3 text-xs text-stone-500">
              Inga extraherade fält att visa för den här avtalstypen.
            </div>
          )}
        </aside>

        <main className="flex flex-col overflow-hidden bg-stone-100">
          <div className="flex items-center justify-between gap-2 border-b border-stone-200 bg-white px-4 py-2">
            <span className="text-xs text-stone-500 truncate">
              {original.filename}
            </span>
            <a
              href={original.url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded border border-stone-300 px-2 py-1 text-xs text-stone-700 hover:bg-stone-50"
            >
              Öppna i ny flik ↗
            </a>
          </div>
          {/* Mobile/tablet — the inline iframe is unreadable below the
              side-by-side breakpoint. Surface a single, prominent CTA
              instead and let the browser open the PDF natively. */}
          <a
            href={original.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-start gap-2 p-4 text-sm text-stone-700 hover:bg-stone-100/60 lg:hidden"
          >
            <span className="font-medium text-stone-900">
              Öppna originaldokumentet
            </span>
            <span className="text-xs text-stone-500">
              Förhandsvisning visas i ett separat fönster på mindre skärmar.
            </span>
          </a>
          {/* Desktop inline iframe — only useful when the right column has
              real estate to render an A4 page. */}
          <div className="hidden lg:block lg:flex-1 lg:overflow-hidden lg:p-4">
            <iframe
              title="Originaldokument"
              src={original.url}
              className="h-full w-full rounded border border-stone-200 bg-white"
            />
          </div>
        </main>
      </div>
        </>
      )}
    </div>
  );
}

/**
 * Property-link confirmation banner driven by the warnings the
 * imports.confirm step emits. Four states based on `code`:
 *
 *   - `property_auto_linked`        — already-existing property linked.
 *                                     Green confirm: "ser detta rätt ut?".
 *   - `property_auto_created`       — new property created from a clean
 *                                     extraction. Green confirm with a
 *                                     "Redigera fastighet" affordance.
 *   - `property_soft_duplicates_found` — Nova extracted enough but the
 *                                     catalog has similar entries. Yellow
 *                                     prompt nudges the user to choose
 *                                     "Anslut till befintlig" vs "Skapa
 *                                     ny" — surfaces the PropertyAutofillGroup
 *                                     editor mode in a future PR.
 *   - `property_link_pending`       — extraction was partial. Yellow CTA to
 *                                     fill in + link manually.
 *
 * No code present (or extracted property is fully empty) → no banner.
 * The bulk-upload preview can map these codes to per-row chips later.
 */
function PropertyConfirmBanner({
  contract,
  original,
}: {
  contract: ContractDetail;
  original: NonNullable<ContractDetail['originalDocument']>;
}) {
  const propertyWarning = original.warnings.find((w) =>
    w.code.startsWith('property_'),
  );
  if (!propertyWarning) return null;

  if (
    propertyWarning.code === 'property_auto_linked' ||
    propertyWarning.code === 'property_auto_created'
  ) {
    // No CTA — the property fields render inline in the section list
    // below (property_designation, property_address, etc. via
    // scopes.imported). A separate "review" button would just point at
    // what the user is already looking at.
    return (
      <div className="mb-4 rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
        <p className="font-medium">Fastighet ansluten automatiskt — ser detta rätt ut?</p>
        <p className="mt-1 text-xs">{propertyWarning.message}</p>
      </div>
    );
  }

  // property_soft_duplicates_found OR property_link_pending — both
  // need user action. Banner copy mirrors the backend message; the
  // CTA is the same "fix it in the editor" affordance until the
  // dedicated PropertyAutofillGroup integration lands.
  return (
    <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-medium">Fastighet behöver bekräftas.</p>
      <p className="mt-1 text-xs">{propertyWarning.message}</p>
      <p className="mt-2 text-xs text-amber-800">
        Klicka <span className="font-medium">Komplettera fälten</span> nedan för
        att ansluta eller skapa en fastighet manuellt.
      </p>
    </div>
  );
}
